/**
 * Email campaign attribution (privacy policy §3.10).
 *
 * Links in our emails carry utm_* parameters. The app keeps them for the lifetime of the
 * browser tab (sessionStorage, no cookies) and stores them with the account at sign-up
 * (record_signup_campaign) and with the subscription at checkout (Stripe metadata).
 */

export interface Campaign {
  source?: string
  medium?: string
  campaign: string
  content?: string
}

const STORAGE_KEY = 'lexalert.campaign'
const RECORDED_KEY = 'lexalert.campaign.recorded'

/** Read utm_* from the current URL into sessionStorage (the latest campaign link wins) */
export function captureCampaign(): void {
  try {
    const params = new URLSearchParams(window.location.search)
    const campaign = params.get('utm_campaign')?.trim()
    if (!campaign) return
    const value: Campaign = {
      campaign,
      source: params.get('utm_source')?.trim() || undefined,
      medium: params.get('utm_medium')?.trim() || undefined,
      content: params.get('utm_content')?.trim() || undefined,
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value))
    sessionStorage.removeItem(RECORDED_KEY)
  } catch {
    // storage unavailable: attribution is best effort
  }
}

/** The campaign this tab arrived with, if any */
export function getCampaign(): Campaign | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Campaign
    return value?.campaign ? value : null
  } catch {
    return null
  }
}

/** Whether the sign-up campaign was already sent for this tab */
export function isCampaignRecorded(): boolean {
  try {
    return sessionStorage.getItem(RECORDED_KEY) === '1'
  } catch {
    return true
  }
}

export function markCampaignRecorded(): void {
  try {
    sessionStorage.setItem(RECORDED_KEY, '1')
  } catch {
    // ignore
  }
}
